import Loader from "react-loader-spinner";
import React, {
    useState,
    useRef,
    useEffect,
    useMemo
} from "react";

export default function SelectInput({
    field,
    error,
    options = [],
    loadOptions,
    defaultLabel,
    value,
    onChange: externalOnChange,
    placeholder,
    rtl,
    itemHeight = 36,
    height = 240
}) {

    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [asyncOptions, setAsyncOptions] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [isLoading, setLoading] = useState(false);

    const containerRef = useRef(null);
    const scrollRef = useRef(null);
    const debounceRef = useRef(null);
    const cacheRef = useRef({});
    const inputRef = useRef(null);

    const effectiveOptions = loadOptions ? asyncOptions : options;
    const currentValue =
        value !== undefined ? value : field?.value ?? "";
    // =====================
    // debounce async search
    // =====================
    const triggerSearch = (value) => {

        if (!loadOptions) return;

        if (cacheRef.current[value]) {
            setAsyncOptions(cacheRef.current[value]);
            return;
        }

        clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            const res = await loadOptions(value);
            setLoading(false);
            cacheRef.current[value] = res || [];
            setAsyncOptions(res || []);

        }, 600);
    };

    // =====================
    // close outside click
    // =====================
    useEffect(() => {

        const handler = (e) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);

    }, []);

    // =====================
    // filtered options
    // =====================
    const filtered = useMemo(() => {

        const term = search.toLowerCase();

        if (!term) return effectiveOptions;

        return effectiveOptions.filter(o =>
            o.label.toLowerCase().includes(term)
        );

    }, [search, effectiveOptions]);

    const selectedLabel = useMemo(() => {

        return effectiveOptions.find(
            o => o.value === currentValue
        )?.label || defaultLabel || "";

    }, [effectiveOptions, field?.value, defaultLabel]);

    // =====================
    // scroll virtualization
    // =====================
    const visibleCount = Math.ceil(height / itemHeight);

    const startIndex = Math.max(
        0,
        highlightedIndex - Math.floor(visibleCount / 2)
    );

    const visibleItems = filtered.slice(
        startIndex,
        startIndex + visibleCount
    );

    // =====================
    // scroll sync
    // =====================
    useEffect(() => {

        if (!scrollRef.current) return;

        const top = highlightedIndex * itemHeight;

        scrollRef.current.scrollTop = top;

    }, [highlightedIndex]);

    // =====================
    // actions
    // =====================
    const selectOption = (opt) => {

        field?.onChange(opt.value);
        externalOnChange?.(opt);

        setSearch(opt.label);
        setIsOpen(false);
    };

    const handleChange = (e) => {

        const val = e.target.value;

        setSearch(val);
        setIsOpen(true);
        setHighlightedIndex(0);

        triggerSearch(val);
    };

    const handleKeyDown = (e) => {

        switch (e.key) {

            case "ArrowDown":
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(prev =>
                    Math.min(prev + 1, filtered.length - 1)
                );
                break;

            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex(prev =>
                    Math.max(prev - 1, 0)
                );
                break;

            case "Enter":
                e.preventDefault();
                selectOption(filtered[highlightedIndex]);
                break;

            case "Escape":
                setIsOpen(false);
                break;
        }
    };

    return (
        <div ref={containerRef} className="position-relative searchable-select">

            <input
                ref={inputRef}
                value={isOpen ? search : selectedLabel}
                onChange={handleChange}
                onFocus={() => setIsOpen(true)}
                onClick={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className={`form-control ${rtl ? "text-end" : ""} ${error ? "is-invalid" : ""}`}
                autoComplete="off"
            />

            {error && (
                <div className="invalid-feedback">
                    {error.message}
                </div>
            )}

            {isOpen && (
                <div
                    ref={scrollRef}
                    className="dropdown-menu show w-100"
                    style={{
                        maxHeight: height,
                        overflowY: "auto",
                        position: "absolute",
                        zIndex: 9999,
                        textAlign: "right"
                    }}
                >
                    {isLoading ?
                        <div className="p-2">
                            <Loader width={18} height={18} type="BallTriangle" color="#bb1370" />
                        </div> :
                        <>
                            {
                                visibleItems.length > 0 ?
                                    <>
                                        {visibleItems.map((opt, i) => {

                                            const realIndex = startIndex + i;

                                            return (
                                                <div
                                                    key={opt.value}
                                                    className={`dropdown-item ${realIndex === highlightedIndex ? "active" : ""} visible-item`}
                                                    onMouseDown={() => selectOption(opt)}
                                                    onMouseEnter={() => setHighlightedIndex(realIndex)}
                                                >
                                                    {opt.label}
                                                </div>
                                            );

                                        })}</> : <div className="visible-item text-center" style={{color:"gray"}}>داده‌ای برای نمایش وجود ندارد</div>}</>}

                </div>
            )}

        </div>
    );
}